import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleTypeahead } from '../BubbleUI';
import {
  Brand,
  BrandContentAttribute,
  BrandContentLevel,
  BrandContentSegment,
} from '@go-shared/types/brand';
import { updateBrand } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import { ObjectId } from 'bson';

interface BrandContentLibraryFormDataBase {
  contentSegments: BrandContentSegment[];
  contentLevels: BrandContentLevel[];
  contentAttributes: BrandContentAttribute[];
}

type BrandContentLibraryFormData = BrandContentLibraryFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandContentLibraryFormError>;
};

type BrandContentLibraryFormError = { [K in keyof BrandContentLibraryFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandContentLibraryFormData {
  return {
    contentSegments: brand ? brand.contentSegments : [],
    contentLevels: brand ? brand.contentLevels : [],
    contentAttributes: brand ? brand.contentAttributes : [],

    _dirty: false,
    _errors: {},
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandContentLibraryForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandContentLibraryFormData>(
    DEFAULT_FORM_DATA(props.edit)
  );

  const segmentOpts = formData.contentSegments.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const levelOpts = formData.contentLevels.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const attributeOpts = formData.contentAttributes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandContentLibraryFormDataBase>(
    key: T,
    val: BrandContentLibraryFormDataBase[T]
  ) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,

          contentSegments: formData.contentSegments,
          contentLevels: formData.contentLevels,
          contentAttributes: formData.contentAttributes,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Segments" className="col-span-3">
        <BubbleTypeahead
          block
          options={segmentOpts}
          placeholder="Enter segment name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={segmentOpts.length > 0}
          suggestionsText="No similar segments found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const arIdx = formData.contentSegments.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newSegs = [...formData.contentSegments];
                  newSegs.splice(arIdx, 1, { ...opt.item, name: val });
                  changeForm('contentSegments', newSegs);
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy mr-3">Color</span>
              <BubbleInput
                type="color"
                value={opt.item.color}
                onValue={val => {
                  const arIdx = formData.contentSegments.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newSegs = [...formData.contentSegments];
                  newSegs.splice(arIdx, 1, { ...opt.item, color: val });
                  changeForm('contentSegments', newSegs);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('contentSegments', [
              ...formData.contentSegments,
              { _id: new ObjectId().toHexString(), name: val, color: '#000000' },
            ])
          }
          onTrash={val =>
            changeForm(
              'contentSegments',
              formData.contentSegments.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Levels" className="col-span-3">
        <BubbleTypeahead
          block
          options={levelOpts}
          placeholder="Enter level name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={levelOpts.length > 0}
          suggestionsText="No similar levels found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const lIdx = formData.contentLevels.findIndex(l => l._id === opt.item._id);
                  if (lIdx < 0) return;
                  const newLevels = [...formData.contentLevels];
                  newLevels.splice(lIdx, 1, { ...opt.item, name: val });
                  changeForm('contentLevels', newLevels);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('contentLevels', [
              ...formData.contentLevels,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'contentLevels',
              formData.contentLevels.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Attributes" className="col-span-3">
        <BubbleTypeahead
          block
          options={attributeOpts}
          placeholder="Enter attribue name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={attributeOpts.length > 0}
          suggestionsText="No similar attributes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const attrIdx = formData.contentAttributes.findIndex(a => a._id === opt.item._id);
                  if (attrIdx < 0) return;
                  const newAttrs = [...formData.contentAttributes];
                  newAttrs.splice(attrIdx, 1, { ...opt.item, name: val });
                  changeForm('contentAttributes', newAttrs);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('contentAttributes', [
              ...formData.contentAttributes,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'contentAttributes',
              formData.contentAttributes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
